import axios from 'axios'
import { createAsyncThunk } from '@reduxjs/toolkit'

const baseUrl = process.env.REACT_APP_MAP_SERVER_API_URL ?? ''
// Default keeps the app working even when the env var isn't set in a given
// deploy. Override in .env for staging/dev.
const companyId = process.env.REACT_APP_COMPANY_ID ?? '69d7925e89d8d58bc444ba89'

// Defensive item extraction: the public endpoint should hand back a flat array,
// but tolerate a wrapped { data: { items } } / { items } shape in case the
// backend evolves.
function extractItems(payload) {
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload?.data?.items)) return payload.data.items
  if (Array.isArray(payload?.items)) return payload.items
  if (Array.isArray(payload?.data)) return payload.data
  return []
}

// Normalise the API's vehicle type into one of the three canonical values
// the downstream code expects:
//   - 'CAR'     → setVehiclesLayer's `vehicle_type == 'CAR'` branch (car icons)
//   - 'BIKE'    → bike branch (bike icons)
//   - 'SCOOTER' → default branch (e-scooter icons); markerPopup will prepend
//                 "E-" via the propulsion_type === 'electric' check
// The API currently sends 'e-scooter' which would (a) miss the layer's CAR/BIKE
// checks and fall into the e-scooter default (still correct visually) but (b)
// produce "E-E-scooter" in the popup because the title-cased type already
// starts with "E-". Normalising here keeps both code paths clean.
function normalizeVehicleType(apiType) {
  const t = String(apiType ?? '').toLowerCase()
  if (t === 'car') return 'CAR'
  if (t === 'bike' || t === 'bicycle') return 'BIKE'
  return 'SCOOTER'
}

// Map one API item to the GeoJSON Feature shape the map layers expect.
// API item shape: { label, bateryPercent (sic), lat, lon, type }
function vehicleToFeature(vehicle) {
  // The backend contract has a typo: `bateryPercent`. Fall back to the correct
  // spelling so this keeps working if it's fixed later.
  const batteryPercent = vehicle.bateryPercent ?? vehicle.batteryPercent ?? 0
  return {
    type: 'Feature',
    geometry: {
      type: 'Point',
      coordinates: [vehicle.lon, vehicle.lat],
    },
    properties: {
      // `label` is the human-readable id shown in the popup and used in the
      // start-ride deep link.
      vehicle_id: vehicle.label,
      // Downstream code (markerPopup, setVehiclesLayer) expects 0–1 and
      // multiplies by 100. The API hands us 0–100, so divide once here.
      battery_pct: batteryPercent / 100,
      vehicle_type: normalizeVehicleType(vehicle.type),
      // propulsion_type isn't in the new response; entire fleet is electric.
      propulsion_type: 'electric',
      // TODO: backend will add a discount field on the vehicle later. When it
      // does, surface it here as `isDiscount: vehicle.<field>` and the existing
      // setVehiclesLayer expression will pick up the reduced-price icon.
    },
  }
}

export const getVehiclesData = createAsyncThunk('vehicles/getVehiclesData', async (_, { rejectWithValue }) => {
  try {
    // Public endpoint — no auth, no credentials.
    // baseUrl already includes the `/api` prefix (see .env), so the path here
    // starts at `/public/...` to avoid a `/api/api/` double-prefix.
    const response = await axios.get(`${baseUrl}/public/companies/${companyId}/vehicles`)
    const items = extractItems(response.data)
    const markers = items.map(vehicleToFeature)

    return {
      type: 'FeatureCollection',
      totalMarkers: markers.length,
      features: markers,
    }
  } catch (error) {
    if (error.response && error.response.data?.message) {
      return rejectWithValue({ data: error.response.data, status: error.response.status })
    }
    return rejectWithValue(error.message)
  }
})
